import React, { useEffect, useState } from "react";
import Loader from "../components/common/loader/loader";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import Error from "../components/common/error/error";
import { listCategoriesForForms } from "../api/admin/categories";
import { notify } from "../lib/utils";

import {
  createSubcategory,
  getSubcategoryById,
  updateSubcategory,
} from "../api/admin/subcategories";
import CreateUpdateSubcategoryForm from "../components/subcategories/create-update-subcategory-form";
import usePageTitle from "../hooks/usePageTitle";

function SubcategoryFormPage() {
  const params = useParams();
  const navigate = useNavigate();

  const [subcategory, setSubcategory] = useState<any>(null);

  const [categories, setCategories] = useState<any>([]); // parent categories

  const [isEditMode, setIsEditMode] = useState(false);

  usePageTitle(
    isEditMode
      ? `Edit Subcategory: ${subcategory?.internalName}`
      : "Create Subcategory"
  );

  const [loading, setLoading] = React.useState(true);

  const [btnLoading, setBtnLoading] = React.useState(false); // when saving

  const [error, setError] = React.useState<any>(false);

  const loadSubcategoryById = async (id: string) => {
    const response = await getSubcategoryById(id);

    if (!response || response.status !== 200) {
      setError("Error loading Category");
      setLoading(false);
      setIsEditMode(false);
      return;
    }

    setIsEditMode(true);
    setSubcategory(response.data);
    setError(null);
    setLoading(false);
  };

  const loadCategoriesForForm = async () => {
    const response = await listCategoriesForForms();

    if (!response || response.status !== 200) {
      setError("Error loading Subcategories list");
      return;
    }

    setCategories(response.data);
  };

  useEffect(() => {
    loadCategoriesForForm();

    if (params.id) {
      loadSubcategoryById(params.id);
    } else {
      setIsEditMode(false);
      setLoading(false);
      setError(false);
    }
    // reset the state when unmounting

    return () => {
      setSubcategory(null);
      setLoading(false);
      setError(false);
      setIsEditMode(false);
      setBtnLoading(false);
      setCategories([]);
    };
  }, []);

  // handle subcategory creation
  const handleCreateSubcategory = async ({ subcategoryData }: any) => {
    const response = await createSubcategory({ subcategoryData });

    if (!response || response.status !== 201) {
      // check slug duplication
      if (response?.status === 409) {
        notify({
          type: "error",
          message: "Subcategory already exists",
        });
      } else {
        notify({
          type: "error",
          message: "Error creating Subcategory",
        });
      }

      return;
    }

    notify({
      type: "success",
      message: "Subcategory created successfully",
    });

    // redirect to the Subcategory list page
    navigate(ROUTES.SUBCATEGORIES);
  };

  // handle subcategory creation
  const handleUpdateSubcategory = async ({
    subcategoryId,
    subcategoryData,
    removedCategories,
  }: any) => {
    const obj = {
      subcategoryData,
      removedCategories, // only deleted ones to unlink from templates, new ones don't matter.
    };

    const response = await updateSubcategory(subcategoryId, obj);

    if (!response || response.status !== 200) {
      // check slug duplication
      if (response?.status === 409) {
        notify({
          type: "error",
          message: "Subcategory Slug already exists",
        });
      } else {
        notify({
          type: "error",
          message: "Error updating Subcategory",
        });
      }

      return;
    }

    notify({
      type: "success",
      message: "Subcategory updated successfully",
    });

    // redirect to the category list page
    navigate(ROUTES.SUBCATEGORIES);
  };

  return (
    <div className="w-full h-full">
      {loading && <Loader pageLoader />}

      {!loading && error && <Error message={error} />}

      {!loading && !error && (
        <div className="w-[95%] mx-auto ">
          <div className="w-full mx-auto">
            <h1 className="text-2xl font-bold text-primary">
              {isEditMode
                ? `Edit Subcategory: ${subcategory?.internalName}`
                : "Create Subcategory"}
            </h1>
          </div>

          <div className="w-full block mx-auto my-10 pb-20">
            <CreateUpdateSubcategoryForm
              subcategory={subcategory}
              btnLoading={btnLoading}
              isEditMode={isEditMode}
              categories={categories}
              onFormSubmission={(obj: any) => {
                if (isEditMode) {
                  handleUpdateSubcategory(obj);
                } else {
                  handleCreateSubcategory(obj);
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default SubcategoryFormPage;
